/**
 * Utilidades para formateo de dinero colombiano (COP)
 * En Colombia se usa el punto (.) como separador de miles
 * Ejemplo: $150.000 COP
 */

import React from 'react';

/**
 * Formatea un número a formato de moneda colombiana
 * @param value - Valor numérico a formatear
 * @param includeSymbol - Si se incluye el símbolo $ (por defecto true)
 * @returns String formateado, ej: "$150.000" o "150.000"
 */
export const formatCOP = (value: number | string | null | undefined, includeSymbol: boolean = true): string => {
  if (value === null || value === undefined || value === '') {
    return includeSymbol ? '$0' : '0';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return includeSymbol ? '$0' : '0';
  }

  // Usar toLocaleString con locale español de Colombia
  const formatted = Math.round(numValue).toLocaleString('es-CO');
  
  return includeSymbol ? `$${formatted}` : formatted;
};

/**
 * Parsea un string con formato COP a número
 * Acepta formatos como: "150.000", "$150.000", "150000"
 * @param value - String con formato COP
 * @returns Número parseado
 */
export const parseCOP = (value: string): number => {
  if (!value) return 0;
  
  // Remover símbolo $ y espacios
  let cleaned = value.replace(/\$/g, '').replace(/\s/g, '');
  
  // Remover puntos (separadores de miles)
  cleaned = cleaned.replace(/\./g, '');
  
  // Parsear el número
  const parsed = parseFloat(cleaned);
  
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Formatea un input mientras el usuario escribe
 * Convierte automáticamente a formato COP
 * @param value - Valor del input
 * @returns Valor formateado con puntos de miles
 */
export const formatInputCOP = (value: string): string => {
  if (!value) return '';
  
  // Remover todo excepto dígitos
  const digitsOnly = value.replace(/\D/g, '');
  
  if (!digitsOnly) return '';
  
  // Convertir a número y formatear
  const number = parseInt(digitsOnly, 10);
  
  if (isNaN(number)) return '';
  
  return number.toLocaleString('es-CO');
};

/**
 * Hook personalizado para inputs de dinero
 * Retorna el valor formateado y funciones para manejar cambios
 */
export const useCurrencyInput = (initialValue: number = 0) => {
  const [displayValue, setDisplayValue] = React.useState(formatCOP(initialValue, false));
  const [numericValue, setNumericValue] = React.useState(initialValue);

  const handleChange = (inputValue: string) => {
    const numeric = parseCOP(inputValue);
    setNumericValue(numeric);
    setDisplayValue(formatInputCOP(inputValue));
  };

  const setValue = (value: number) => {
    setNumericValue(value);
    setDisplayValue(formatCOP(value, false));
  };

  return {
    displayValue,
    numericValue,
    handleChange,
    setValue,
  };
};

/**
 * Componente de Input para dinero COP
 */
interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  name?: string;
  id?: string;
  autoFocus?: boolean;
}

export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onChange,
  placeholder = '0',
  className = '',
  required = false,
  disabled = false,
  min,
  max,
  name,
  id,
  autoFocus,
}) => {
  const [displayValue, setDisplayValue] = React.useState(formatCOP(value, false));

  // Sincronizar cuando el valor externo cambia
  React.useEffect(() => {
    setDisplayValue(formatCOP(value, false));
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    
    // Permitir solo dígitos y puntos
    if (inputVal && !/^[\d.]*$/.test(inputVal)) {
      return;
    }

    // Formatear mientras escribe
    const formatted = formatInputCOP(inputVal);
    setDisplayValue(formatted);
    
    // Convertir a número y notificar
    const numeric = parseCOP(inputVal);
    
    // Validar min/max si están definidos
    if (min !== undefined && numeric < min) return;
    if (max !== undefined && numeric > max) return;
    
    onChange(numeric);
  };

  const handleBlur = () => {
    // Al perder el foco, reformatear el valor
    setDisplayValue(formatCOP(value, false));
  };

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">
        $
      </span>
      <input
        type="text"
        id={id}
        name={name}
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`pl-7 ${className}`}
        required={required}
        disabled={disabled}
        inputMode="numeric"
        autoFocus={autoFocus}
      />
    </div>
  );
};

export default {
  formatCOP,
  parseCOP,
  formatInputCOP,
  useCurrencyInput,
  CurrencyInput,
};
